/**
 * Validação de CPF conforme algoritmo da Receita Federal.
 * Rejeita CPFs inválidos (dígitos iguais, sequências conhecidas, dígitos verificadores incorretos).
 */

const CPF_INVALIDOS = new Set([
  '00000000000', '11111111111', '22222222222', '33333333333', '44444444444',
  '55555555555', '66666666666', '77777777777', '88888888888', '99999999999',
]);

/**
 * Valida um CPF (formato ou apenas dígitos).
 * @param cpf - CPF com ou sem formatação
 * @returns true se válido
 */
export function isValidCPF(cpf: string | undefined | null): boolean {
  if (!cpf || typeof cpf !== 'string') return false;

  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (CPF_INVALIDOS.has(digits)) return false;

  // Validar primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i], 10) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9], 10)) return false;

  // Validar segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i], 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10], 10)) return false;

  return true;
}

/**
 * Retorna o CPF apenas com dígitos, ou null se inválido.
 */
export function normalizeCPF(cpf: string | undefined | null): string | null {
  if (!cpf || typeof cpf !== 'string') return null;
  const digits = cpf.replace(/\D/g, '');
  return digits.length === 11 ? digits : null;
}

// --- CNPJ (Pessoa Jurídica) ---

const CNPJ_INVALIDOS = new Set([
  '00000000000000', '11111111111111', '22222222222222', '33333333333333',
  '44444444444444', '55555555555555', '66666666666666', '77777777777777',
  '88888888888888', '99999999999999',
]);

/**
 * Valida um CNPJ (formato ou apenas dígitos) conforme algoritmo da Receita Federal.
 */
export function isValidCNPJ(cnpj: string | undefined | null): boolean {
  if (!cnpj || typeof cnpj !== 'string') return false;

  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (CNPJ_INVALIDOS.has(digits)) return false;

  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i], 10) * pesos1[i];
  }
  let remainder = sum % 11;
  const d1 = remainder < 2 ? 0 : 11 - remainder;
  if (d1 !== parseInt(digits[12], 10)) return false;

  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(digits[i], 10) * pesos2[i];
  }
  remainder = sum % 11;
  const d2 = remainder < 2 ? 0 : 11 - remainder;
  if (d2 !== parseInt(digits[13], 10)) return false;

  return true;
}

/**
 * Retorna o CNPJ apenas com dígitos, ou null se inválido.
 */
export function normalizeCNPJ(cnpj: string | undefined | null): string | null {
  if (!cnpj || typeof cnpj !== 'string') return null;
  const digits = cnpj.replace(/\D/g, '');
  return digits.length === 14 ? digits : null;
}

/**
 * Formata CNPJ para exibição: 00.000.000/0000-00
 */
export function formatCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length >= 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (digits.length > 12) return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d*)$/, '$1.$2.$3/$4-$5');
  if (digits.length > 8) return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d*)$/, '$1.$2.$3/$4');
  if (digits.length > 5) return digits.replace(/^(\d{2})(\d{3})(\d*)$/, '$1.$2.$3');
  if (digits.length > 2) return digits.replace(/^(\d{2})(\d*)$/, '$1.$2');
  return digits;
}
